import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { withRouter } from "react-router-dom/cjs/react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { Modal, ModalHeader } from "reactstrap";
import styled from "styled-components";
import { ShowSearchBar } from "../../../components/SearchBar";
import Breadcrumb from "../../../components/common/breadcrumb";
import { BlurSpinner } from "../../../components/BlurSpinner";
import { tableCustomStylesNP } from "../../../ui/table";
import usePaging from "../../../hooks/usePaging";
import { fetchSubclientesInactivos, patchSubclientesInactivos } from "../../../services/subclientesInactivosServices";
import { ButtonPrimary, ButtonSecondary } from "../../../ui/buttons";
import { formatNumeroDeLegajo } from "../../../components/course/segmentationClientGropuModal/clientGroupForm";
import { useSearchBar } from "../../../components/SearchBar/context/SearchBarContext";
import ConfirmModal from "../../../components/ConfirmModal";
import { useAuth } from "../../../context/AuthContext";
import modulos from "../../../mocks/modulos";

const ModalContainer = styled.div`
  padding: 30px;
  font-family: "Nunito", sans-serif;
`;

const ModalTitle = styled.h4`
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 24px;
  margin-bottom: 20px;
  color: #293134;
`;

const ModalSubtitle = styled.p`
  font-family: "Nunito", sans-serif;
  font-weight: 400;
  font-size: 16px;
  margin-bottom: 15px;
  color: #666666;
`;

const ErrorList = styled.ul`
  margin-bottom: 25px;
  padding-left: 20px;
  list-style-type: disc;
`;

const ErrorItem = styled.li`
  font-family: "Nunito", sans-serif;
  font-weight: 400;
  font-size: 15px;
  margin-bottom: 10px;
  color: #333333;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SinAccesoPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 30px;

  p {
    text-align: center;
    font-size: 20px;
    color: #333333;
  }
`;
const SubclientesInactivos = ({ history }) => {
  // permisos
  const { currentUser } = useAuth();
  const isAdmin = currentUser && currentUser.haveAdminAccess(modulos.CLIENTES);

  const { searchBarText } = useSearchBar();
  const pagingController = usePaging(fetchSubclientesInactivos);
  const [confirmarModal, setConfirmarModal] = useState(false);
  const [subclientToActivate, setSubclientToActivate] = useState(null);
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isActivating, setIsActivating] = useState(false);

  const handleActivateSubclient = async () => {
    setConfirmarModal(false);
    setIsActivating(true);
    if (!subclientToActivate || !subclientToActivate.itemId) {
      toast.error("No se ha seleccionado un subcliente válido para activar.");
      return;
    }
    try {
      const response = await patchSubclientesInactivos(subclientToActivate.itemId);
      if (response.status === 400) {
        setErrors(response.errors || []);
        setShowErrorsModal(true);
        toast.error(response.message || "Error al activar el subcliente");
        return;
      }

      toast.success("Subcliente activado exitosamente");

      pagingController.reloadData(history.location);
    } catch (error) {
      toast.error("Error al activar el subcliente");
    } finally {
      setIsActivating(false);
    }
  };

  useEffect(() => {
    pagingController.setFilterValue({ query: searchBarText });
  }, [searchBarText]);

  if (!isAdmin) {
    return (
      <SinAccesoPageContainer>
        <Helmet>
          <title>Acceso Denegado</title>
        </Helmet>
        <Breadcrumb title="Subclientes Inactivos" parent="Clientes" backButton onBackButton={() => history.push("/clientes")} />
        <h1>Acceso Denegado</h1>
        <p>No tienes permisos para acceder a esta página.</p>
        <ButtonPrimary onClick={() => history.push("/clientes")}>Volver a Clientes</ButtonPrimary>
      </SinAccesoPageContainer>
    );
  }

  return (
    <div>
      <Helmet>
        <title>PIN - Subclientes Inactivos</title>
      </Helmet>
      <ShowSearchBar defaultValue={pagingController.query.query} />
      <Breadcrumb title="Subclientes Inactivos" parent="Clientes" backButton onBackButton={() => history.push("/clientes")} />
      <BlurSpinner isLoading={pagingController.isLoading || isActivating}>
        <div className="table-responsive support-table border">
          <DataTable
            // title="Notas de Pedido"
            paginationComponentOptions={{ rowsPerPageText: "Notas por pagina", rangeSeparatorText: "De", selectAllRowsItemText: "Todos" }}
            noDataComponent={
              <div className="p-3">
                <b>No se encontraron Subclientes Inactivos</b>
              </div>
            }
            striped
            pagination
            sortServer
            paginationServer
            // onRowClicked={handleRowClicked}
            data={pagingController.data}
            onSort={pagingController.handleSortsChange}
            paginationTotalRows={pagingController.totalRows}
            paginationDefaultPage={parseInt(pagingController.query.page || 1, 10)}
            onChangeRowsPerPage={pagingController.handleRecordsPerPageChange}
            paginationPerPage={parseInt(pagingController.query.perpage || 10, 10)}
            onChangePage={pagingController.handlePageChange}
            customStyles={tableCustomStylesNP}
            columns={[
              {
                name: "Razón Social",
                id: "razonSocial",
                selector: (row) => (row.razonSocial ? row.razonSocial : "-"),
                sortable: true,
                center: true,
                style: {
                  cursor: "pointer",
                  textAlign: "center",
                },
              },
              {
                name: "Legajo",
                selector: (row) => (row.numeroDeLegajo ? formatNumeroDeLegajo(row.numeroDeLegajo) : "-"),
                sortable: true,
                id: "numeroDeLegajo",
                center: true,
                style: {
                  cursor: "pointer",
                  textAlign: "center",
                  width: "115px",
                },
              },
              {
                name: "Servicio",
                selector: (row) => (row.servicio2Cat && row.servicio2Cat.text ? row.servicio2Cat.text : "-"),
                center: true,
                id: "servicio2Cat",
                style: {
                  cursor: "pointer",
                  textAlign: "center",
                },
              },
              {
                name: "CUIT",
                selector: (row) => (row.cuit ? row.cuit : "-"),
                sortable: true,
                id: "cuit",
                center: true,
                style: {
                  cursor: "pointer",
                  textAlign: "center",
                  width: "115px",
                },
              },
              {
                name: "Sucursal",
                selector: (row) => (row.Sucursal && (row.Sucursal.title || row.Sucursal.titulo) ? row.Sucursal.title || row.Sucursal.titulo : "-"),
                sortable: true,
                id: "sucursal",
                center: true,
                style: {
                  cursor: "pointer",
                  textAlign: "center",
                  width: "115px",
                },
              },
              {
                name: "Acciones",
                selector: (row) =>
                  row.itemId ? (
                    <div className="d-flex justify-content-center w-100">
                      <ButtonSecondary
                        height="30px"
                        marginRight="0px"
                        onClick={() => {
                          setConfirmarModal(true);
                          setSubclientToActivate(row);
                        }}
                      >
                        Activar
                      </ButtonSecondary>
                    </div>
                  ) : null,
                center: true,
                width: "140px",
              },
            ]}
          />
        </div>
      </BlurSpinner>
      {confirmarModal && (
        <ConfirmModal
          isOpen={confirmarModal}
          toggle={() => {
            setConfirmarModal(!confirmarModal);
          }}
          title="Confirmar Activación"
          text={
            subclientToActivate && subclientToActivate.numeroDeLegajo
              ? `¿Estás seguro que deseas activar el subcliente "${subclientToActivate.razonSocial}" con legajo ${formatNumeroDeLegajo(
                  subclientToActivate.numeroDeLegajo
                )}?`
              : "¿Estás seguro que deseas activar este subcliente?"
          }
          handleContinuar={handleActivateSubclient}
          handleCancelar={() => {
            setSubclientToActivate(null);
            setConfirmarModal(false);
          }}
          canClose
        />
      )}
      {errors && errors.length > 0 && (
        <Modal isOpen={showErrorsModal} toggle={() => setShowErrorsModal(!showErrorsModal)} size="lg" centered>
          <ModalHeader className="border-0" toggle />
          <ModalContainer>
            <ModalTitle>No se pudo activar el subcliente</ModalTitle>
            <ModalSubtitle>Se encontraron los siguientes errores:</ModalSubtitle>
            <ErrorList>
              {errors.map((error) => (
                <ErrorItem key={error}>{error}</ErrorItem>
              ))}
            </ErrorList>
            <ButtonContainer>
              <ButtonPrimary
                onClick={() => {
                  setShowErrorsModal(false);
                  setErrors([]);
                }}
              >
                Cerrar
              </ButtonPrimary>
            </ButtonContainer>
          </ModalContainer>
        </Modal>
      )}
    </div>
  );
};

export default withRouter(SubclientesInactivos);
